import { useState } from 'react';
import { FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal } from '@safetyhub/ui';
import type {
  LegalObligationIntervalVariantView,
  LegalObligationPendingResultView,
} from '@safetyhub/shared';
import { EditModalFooter } from '../common/EditModalFooter';
import { describeMeasurementResult } from '../../lib/legalDocumentLabels';

/** 서버 DTO의 사유 상한(`LEGAL_OBLIGATION_EVIDENCE_RELINK_REASON_MAX`)과 같은 값 — 화면 쪽 안전선 */
const REASON_MAX_LENGTH = 500;

/** 「연결 끊기」 선택지의 값 — 결과 id(CUID)와 겹치지 않는 표지 */
const UNLINK = '__unlink__';

export interface ResultRelinkModalProps {
  open: boolean;
  /** 지금 이어진 측정 결과(없으면 null) — 안내에만 쓴다 */
  current: { entryId: string; code: string; occurredAt: string } | null;
  /** 옮겨 갈 수 있는 결과 — 결과표가 아직 없는 결과(서버 `pendingResults`) */
  pendingResults: readonly LegalObligationPendingResultView[];
  resultVariants: ReadonlyArray<
    Pick<LegalObligationIntervalVariantView, 'selector' | 'valueKeys' | 'label'>
  >;
  submitting: boolean;
  /** 서버 거부 문구 — 모달을 연 채로 보인다 */
  errorMessage: string | null;
  onSubmit: (input: { resultEntryId: string | null; reason: string }) => void;
  onClose: () => void;
}

/**
 * 결과표 ↔ 측정 결과 연결 바꾸기(B+ · 오연결 정정 — 사용자 결정 2026-11-02). 다른 측정을 고르면 서버가 증빙을 그 측정이 든
 * 기간으로 옮기고, 「연결 끊기」면 증빙은 지금 기간에 남고 결과표 표시만 사라진다. 법정 증빙의 귀속을 바꾸는 일이라 **사유가 필수**이고
 * 서버가 감사 기록에 전후와 함께 남긴다(`PUT …/evidences/:evidenceId/result-link`).
 */
export default function ResultRelinkModal({
  open,
  current,
  pendingResults,
  resultVariants,
  submitting,
  errorMessage,
  onSubmit,
  onClose,
}: ResultRelinkModalProps) {
  const [choice, setChoice] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const trimmedReason = reason.trim();
  const choices = pendingResults.filter((r) => r.entryId !== current?.entryId);

  const isDirty = choice !== null || trimmedReason !== '';
  const blockedReason = !choice
    ? '이을 측정을 고르거나 연결 끊기를 고르세요.'
    : !trimmedReason
      ? '연결을 바꾸는 사유를 적으세요.'
      : '';

  const handleSubmit = () => {
    if (blockedReason || submitting || !choice) return;
    onSubmit({ resultEntryId: choice === UNLINK ? null : choice, reason: trimmedReason });
  };

  const option = (value: string, label: string) => (
    <li key={value}>
      <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-line px-3 py-2 hover:bg-surface-hover">
        <input
          type="radio"
          name="legal-evidence-relink"
          value={value}
          checked={choice === value}
          onChange={() => setChoice(value)}
          disabled={submitting}
          className="mt-1 accent-brand-600"
        />
        <span className="text-sm text-ink-900">{label}</span>
      </label>
    </li>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="결과표 연결 바꾸기"
      size="md"
      closeLocked={submitting}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason}
          submitLabel="연결 바꾸기"
          submitDisabled={!!blockedReason || submitting}
          busy={submitting}
          busyLabel="바꾸는 중..."
          onSubmit={handleSubmit}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          {current ? (
            <>
              지금은{' '}
              <span className="font-bold">
                {describeMeasurementResult(current, resultVariants)}
              </span>{' '}
              측정의 결과표로 등록되어 있습니다.
            </>
          ) : (
            '지금은 어느 측정의 결과표로도 이어져 있지 않습니다.'
          )}{' '}
          다른 측정을 고르면 이 문서가 그 측정이 든 기간의 기록으로 옮겨집니다.
        </p>

        <fieldset>
          <legend className={FORM_LABEL_CLASS}>이을 측정</legend>
          {choices.length === 0 && (
            <p className="mb-2 text-sm text-ink-500">결과표가 없는 다른 측정이 없습니다.</p>
          )}
          <ul className="space-y-1">
            {choices.map((r) => option(r.entryId, describeMeasurementResult(r, resultVariants)))}
            {current && option(UNLINK, '연결 끊기 — 측정 결과표가 아닙니다')}
          </ul>
        </fieldset>

        <div>
          <label htmlFor="legal-evidence-relink-reason" className={FORM_LABEL_CLASS}>
            사유 (필수 — 감사 기록에 남습니다)
          </label>
          <textarea
            id="legal-evidence-relink-reason"
            className={FORM_TEXTAREA_CLASS}
            value={reason}
            maxLength={REASON_MAX_LENGTH}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            placeholder="예: 10월 측정의 결과표를 11월 측정에 잘못 등록함"
          />
        </div>

        {errorMessage && (
          <p
            role="alert"
            className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
